import { Injectable, OnDestroy } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import { ShowIfHttpError } from '@certemy/common';
import { EMPTY, NEVER, Observable, of, throwError, zip } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { skipModalClose, SmartForm, SmartModalService } from '@certemy/ui-components';
import { DEFAULT_MODAL_OPTIONS } from 'constants/constants';
import {
  Country,
  CountryFieldOption,
  INITIAL_ORGANIZATION_OFFICE,
  LOCATION_TYPES,
  LOCATION_VALIDATION_ERROR,
  LOCATION_VALIDATION_FORCIBLY_SHOW_WARNING,
  LOCATION_VALIDATION_RULES,
  LOCATION_VALIDATION_RULES_ACTIONS,
  LocationCountryPayload,
  LocationRegionPayload,
  Office,
  Region,
  replaceLocationOrganizationLabel,
} from 'services/locations/locations.constant';
import _get from 'lodash-es/get';
import { LocationMultiCollapsedPanelData } from 'features/organization-settings/organizations-settings-locations/organizations-settings-locations.constants';
import { Store } from '@ngrx/store';
import {
  DEFAULT_LABEL_SETTINGS,
  GlobalOrganizationsLabel,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import { Selectors as LabelSelectors } from 'features/global-organizations-setting-state/global-organizations-setting.state';
import { buildQueryParams, createQueryParam, sortItemByName } from 'utils/helpers';
import { RowsResponse } from 'interfaces/common.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable()
export class LocationsService implements OnDestroy {
  _labels: GlobalOrganizationsLabel = DEFAULT_LABEL_SETTINGS;

  _getLabelsRequest$(): Observable<GlobalOrganizationsLabel> {
    return this.store.select(LabelSelectors.labels).pipe(
      untilDestroyed(this),
      tap((labels: GlobalOrganizationsLabel) => (this._labels = labels)),
    );
  }

  getLabels$(): Observable<GlobalOrganizationsLabel> {
    if (this._labels) {
      return of(this._labels);
    }
    return this._getLabelsRequest$();
  }

  constructor(private http: HttpService, private smartModalService: SmartModalService, private store: Store<any>) {
    this._getLabelsRequest$().subscribe();
  }

  ngOnDestroy() {}

  @ShowIfHttpError('An error occurred while getting countries')
  getLocationCountries(): Observable<Country[]> {
    return this.http
      .get('/location/countries')
      .pipe(map((response: RowsResponse<Country>) => sortItemByName(response.rows)));
  }

  @ShowIfHttpError('An error occurred while getting organization countries')
  getLocationOrganizationCountries(params = {}): Observable<Country[]> {
    return this.http
      .get('/location/organization/countries', { params })
      .pipe(map((response: RowsResponse<Country>) => sortItemByName(response.rows)));
  }

  @ShowIfHttpError('An error occurred while getting organization countries')
  getLocationOrganizationCountryStates(countyId: number): Observable<any> {
    return this.http
      .get(`/location/organization/country/${countyId}/states`)
      .pipe(map((response: RowsResponse<any>) => sortItemByName(response.rows)));
  }

  @ShowIfHttpError('An error occurred while deleting organization country')
  deleteLocationOrganizationCountry(id: number): Observable<any> {
    return this.http.delete(`/location/organization/country/${id}`);
  }

  @ShowIfHttpError('An error occurred while getting organization regions')
  getLocationOrganizationRegions(ids: number[], addParams = {}): Observable<any> {
    return this.http
      .get(`/location/organization/country/regions`, {
        params: {
          ids,
          ...addParams,
        },
      })
      .pipe(map((response: RowsResponse<SmartForm.FieldOption>) => sortItemByName(response.rows)));
  }

  @ShowIfHttpError('An error occurred while getting organization regions')
  getLocationOrganizationRegionsByState(stateId: number): Observable<any> {
    return this.http.get(`/location/organization/state/${stateId}/regions`).pipe(
      map((response) => sortItemByName(response, 'regionName')),
      map((regions) => regions.map((region) => ({ id: region.regionId, name: region.regionName }))),
    );
  }

  @ShowIfHttpError('An error occurred while getting organization region')
  getLocationOrganizationRegion(regionId: number): Observable<any> {
    return this.http.get(`/location/organization/region/${regionId}`);
  }

  @ShowIfHttpError('An error occurred while creating organization country')
  createLocationOrganizationCountry(payload: LocationCountryPayload): Observable<any> {
    return this.http.post('/location/organization/country', payload);
  }

  @ShowIfHttpError('An error occurred while creating organization region')
  createLocationOrganizationRegion(payload: LocationRegionPayload): Observable<any> {
    return this.http.post('/location/organization/region', payload);
  }

  @ShowIfHttpError('An error occurred while updating organization region')
  updateLocationOrganizationRegion(id, payload: LocationRegionPayload): Observable<any> {
    return this.http.put(`/location/organization/region/${id}`, payload);
  }

  @ShowIfHttpError('An error occurred while deleting organization region')
  deleteLocationOrganizationRegion(id: number): Observable<any> {
    return this.http.delete(`/location/organization/region/${id}`);
  }

  @ShowIfHttpError('An error occurred while getting organization region offices')
  getLocationOrganizationOffices(ids: number[]): Observable<SmartForm.FieldOption[]> {
    return this.http
      .get(`/location/organization/region/offices`, {
        params: {
          ids,
        },
      })
      .pipe(map((response: RowsResponse<SmartForm.FieldOption>) => sortItemByName(response.rows)));
  }

  @ShowIfHttpError('An error occurred while getting organization region offices')
  getLocationOrganizationOfficesByState(stateId: number, regionId: number = null): Observable<any> {
    const params = [createQueryParam('regionIds', regionId)];
    const queryParams = buildQueryParams(params);
    return this.http.get(`/location/organization/state/${stateId}/offices${queryParams}`).pipe(
      map((response) => sortItemByName(response, 'officeName')),
      map((offices) => offices.map((office) => ({ id: office.officeId, name: office.officeName }))),
    );
  }

  @ShowIfHttpError('An error occurred while getting organization office')
  getLocationOrganizationOffice(officeId: number): Observable<any> {
    return this.http.get(`/location/organization/office/${officeId}`);
  }

  @ShowIfHttpError('An error occurred while creating organization office')
  createLocationOrganizationOffice(payload: Office): Observable<any> {
    return this.http.post('/location/organization/office', payload);
  }

  @ShowIfHttpError('An error occurred while updating organization office')
  updateLocationOrganizationOffice(id, payload: Office): Observable<any> {
    return this.http.put(`/location/organization/office/${id}`, payload);
  }

  @ShowIfHttpError('An error occurred while deleting organization region')
  deleteLocationOrganizationOffice(id: number): Observable<any> {
    return this.http.delete(`/location/organization/office/${id}`);
  }

  openCountryModal(component) {
    return this.getLocationCountries().pipe(
      take(1),
      switchMap((countries: Country[]) =>
        this.smartModalService.open(component, { countries }, { ...DEFAULT_MODAL_OPTIONS, size: 'lg' }),
      ),
      skipModalClose(),
      catchError(() => NEVER),
    );
  }

  openRegionModal(data: LocationMultiCollapsedPanelData, component) {
    let regionData$: Observable<Region>;
    let countries$: Observable<CountryFieldOption[]>;
    let states$: Observable<SmartForm.FieldOption[]>;
    if (data && data.id) {
      const { id, name, code, locationTypeId, parent } = data;
      switch (locationTypeId) {
        case LOCATION_TYPES.COUNTRY:
          countries$ = of([
            {
              id,
              name,
              code,
            },
          ]);
          states$ = this.getLocationOrganizationCountryStates(id);
          regionData$ = of(null);
          break;
        default:
          // mean LOCATION_TYPES.REGION
          countries$ = of([
            {
              id: parent.id,
              name: parent.name,
              code: parent.code,
            },
          ]);
          states$ = this.getLocationOrganizationCountryStates(parent.id);
          regionData$ = this.getLocationOrganizationRegion(id);
      }
    } else {
      countries$ = this.getLocationOrganizationCountries();
      states$ = of([]);
      regionData$ = of(null);
    }
    return zip(countries$, states$, regionData$).pipe(
      take(1),
      switchMap(([countries, states, regionData]) =>
        this.smartModalService.open(component, { data, countries, states, regionData }, { size: 'lg' }),
      ),
      skipModalClose(),
      catchError(() => NEVER),
    );
  }

  openOfficeModal(data: LocationMultiCollapsedPanelData, component) {
    let countries$: Observable<CountryFieldOption[]> = this.getLocationOrganizationCountries({ withRegions: true });
    let states$: Observable<SmartForm.FieldOption[]> = of([]);
    let regions$: Observable<SmartForm.FieldOption[]> = of([]);
    let office$: Observable<Office> = of(INITIAL_ORGANIZATION_OFFICE);
    let editMode = false;

    if (data) {
      const { locationTypeId } = data;
      switch (locationTypeId) {
        case LOCATION_TYPES.REGION: {
          // add office
          const { id, name, stateId, parent: country } = data;
          countries$ = of([{ id: country.id, name: country.name, code: country.code }]);
          regions$ = of([{ id, name }]);
          states$ = this.getLocationOrganizationCountryStates(country.id);
          office$ = of({
            ...INITIAL_ORGANIZATION_OFFICE,
            organizationCountryId: country.id,
            organizationRegionId: id,
            stateId,
          });
          break;
        }

        default: {
          // edit office
          const {
            name,
            city,
            address1,
            address2,
            stateId,
            stateName,
            postalCode,
            parent: { id: regionId, name: regionName, parent: country },
          } = data;
          countries$ = of([{ id: country.id, name: country.name, code: country.code }]);
          regions$ = of([
            ...country.children.map(item => ({
              id: item.id,
              name: item.name,
              regionTypeId: item.regionTypeId,
              stateId: item.stateId,
            })),
          ]);
          states$ = this.getLocationOrganizationCountryStates(country.id);
          office$ = of({
            ...INITIAL_ORGANIZATION_OFFICE,
            organizationCountryId: country.id,
            organizationRegionId: regionId,
            name,
            city,
            address1,
            postalCode,
            address2,
            stateId,
            stateName,
          });
          editMode = true;
        }
      }
    }

    return zip(countries$, regions$, states$, office$).pipe(
      take(1),
      switchMap(([countries, regions, states, office]) => {
        return this.smartModalService.open(
          component,
          { countries, regions, states, office, editMode, data },
          { size: 'lg' },
        );
      }),
      skipModalClose(),
      catchError(() => NEVER),
    );
  }

  notificationPopupWrapperWithPreliminaryRequests(
    apiRequest$: Observable<any>,
    location: LOCATION_TYPES,
    type: LOCATION_VALIDATION_RULES_ACTIONS,
  ) {
    let forcibly_show_warning = null;
    if (_get(LOCATION_VALIDATION_FORCIBLY_SHOW_WARNING, `${location}.${type}`)) {
      forcibly_show_warning = {
        activeProfileAvailable: true,
        existAssociatedProfile: true,
      };
    }

    let labels;

    return this.getLabels$().pipe(
      tap((data) => (labels = data)),
      switchMap(() => this.showModalOnChange(location, type, labels)),
      switchMap((result) => result ? apiRequest$ : EMPTY),
      take(1),
      catchError((err) => {
        const errorDetails = _get(err, 'body.error_details');
        if (
          errorDetails &&
          (errorDetails.activeAdminsNumber || errorDetails.activeProfessionalsNumber) &&
          type === LOCATION_VALIDATION_RULES_ACTIONS.EDIT
        ) {
          const body = replaceLocationOrganizationLabel(
            _get(LOCATION_VALIDATION_RULES, `${location}.${type}`),
            labels,
            errorDetails,
          );
          return this.smartModalService
            .openNotification({
              title: 'Attention',
              body,
              closeButtonText: 'Ok',
            })
            .pipe(switchMap(() => throwError(LOCATION_VALIDATION_ERROR)));
        }
        return throwError(err);
      }),
      switchMap((response) => {
        if (
          type === LOCATION_VALIDATION_RULES_ACTIONS.ADD &&
          (_get(response, 'activeProfileAvailable') || forcibly_show_warning)
        ) {
          const body = replaceLocationOrganizationLabel(
            _get(LOCATION_VALIDATION_RULES, `${location}.${type}`),
            labels,
            forcibly_show_warning || response,
          );
          return this.smartModalService.openNotification({
            title: 'Attention',
            body,
            closeButtonText: 'Ok',
          });
        }
        return of(null);
      }),
    );
  }

  showModalOnChange(
    location: LOCATION_TYPES,
    type: LOCATION_VALIDATION_RULES_ACTIONS,
    labels: GlobalOrganizationsLabel
  ): Observable<boolean> {
    if (type === LOCATION_VALIDATION_RULES_ACTIONS.CHANGE) {
      const body = replaceLocationOrganizationLabel(
        _get(LOCATION_VALIDATION_RULES, `${location}.${type}`),
        labels,
      );
      return this.smartModalService.openConfirm({
        title: 'Attention',
        body,
        successButtonText: 'Continue'
      });
    }
    return of(true);
  }
}
